import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Dashboard from './pages/Dashboard'
import Leads from './pages/Leads'
import Deals from './pages/Deals'
import Pipelines from './pages/Pipelines'
import PipelineManagement from './pages/PipelineManagement'
import Messaging from './pages/Messaging'
import Contacts from './pages/Contacts'
import Templates from './pages/Templates'
import Reports from './pages/Reports'
import ScheduledReports from './pages/ScheduledReports'
import Analytics from './pages/Analytics'
import EventLog from './pages/EventLog'
import Performance from './pages/Performance'
import Activities from './pages/Activities'
import Automations from './pages/Automations'
import Integrations from './pages/Integrations'
import CustomFields from './pages/CustomFields'
import Webhooks from './pages/Webhooks'
import OutgoingWebhooks from './pages/OutgoingWebhooks'
import Settings from './pages/Settings'
import AISettings from './pages/AISettings'
import { AIAgents } from './pages/AIAgents'
import PendingContacts from './pages/PendingContacts'
import CompanyUsers from './pages/CompanyUsers'
import Teams from './pages/Teams'
import LeadDistribution from './pages/LeadDistribution'
import Billing from './pages/Billing'
import APIKeys from './pages/APIKeys'

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ marginBottom: 'var(--space-4)' }}></div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Carregando...
          </p>
        </div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

const PermissionRoute = ({ 
  children, 
  permission 
}: { 
  children: React.ReactNode
  permission?: string 
}) => {
  const { hasPermission, isAdmin } = usePermissions()
  
  // Se não tem permissão especificada, permite (apenas auth é necessário)
  if (!permission) {
    return <>{children}</>
  }
  
  // Admin sempre tem acesso
  if (isAdmin) {
    return <>{children}</>
  }
  
  // Verifica se tem a permissão específica
  if (!hasPermission(permission)) {
    return (
      <Navigate to="/" replace />
    )
  }
  
  return <>{children}</>
}

const App = () => {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <Router>
          <Routes>
            {/* Rotas públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Signup />} />

            {/* Rotas protegidas com Layout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<Leads />} />
              <Route path="deals" element={<Deals />} />
              <Route path="pipelines" element={<Pipelines />} />
              <Route path="pipelines/management" element={<PipelineManagement />} />
              <Route path="messaging" element={<Messaging />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="templates" element={<Templates />} />
              <Route path="reports" element={<Reports />} />
              <Route path="scheduled-reports" element={<ScheduledReports />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="event-log" element={<EventLog />} />
              <Route path="performance" element={<Performance />} />
              <Route path="activities" element={<Activities />} />
              <Route 
                path="automations" 
                element={
                  <PermissionRoute permission="automations">
                    <Automations />
                  </PermissionRoute>
                } 
              />
              <Route 
                path="integrations" 
                element={
                  <PermissionRoute permission="integrations">
                    <Integrations />
                  </PermissionRoute>
                } 
              />
              <Route
                path="webhooks"
                element={
                  <PermissionRoute permission="webhooks">
                    <Webhooks />
                  </PermissionRoute>
                }
              />
              <Route
                path="outgoing-webhooks"
                element={
                  <PermissionRoute permission="webhooks">
                    <OutgoingWebhooks />
                  </PermissionRoute>
                }
              />
              <Route 
                path="custom-fields" 
                element={
                  <PermissionRoute permission="custom-fields">
                    <CustomFields />
                  </PermissionRoute>
                } 
              />
              <Route 
                path="settings" 
                element={
                  <PermissionRoute permission="settings">
                    <Settings />
                  </PermissionRoute>
                } 
              />
              <Route 
                path="ai-settings" 
                element={
                  <PermissionRoute permission="ai-settings">
                    <AISettings />
                  </PermissionRoute>
                } 
              />
              <Route 
                path="ai-agents" 
                element={
                  <PermissionRoute permission="ai-agents">
                    <AIAgents />
                  </PermissionRoute>
                } 
              />
              <Route path="pending-contacts" element={<PendingContacts />} />
              <Route 
                path="company-users" 
                element={
                  <PermissionRoute permission="company-users">
                    <CompanyUsers />
                  </PermissionRoute>
                } 
              />
              <Route
                path="teams"
                element={
                  <PermissionRoute permission="teams">
                    <Teams />
                  </PermissionRoute>
                }
              />
              <Route
                path="lead-distribution"
                element={
                  <PermissionRoute permission="settings">
                    <LeadDistribution />
                  </PermissionRoute>
                }
              />
              <Route
                path="billing"
                element={
                  <PermissionRoute permission="settings">
                    <Billing />
                  </PermissionRoute>
                }
              />
              <Route
                path="api-keys"
                element={
                  <PermissionRoute permission="settings">
                    <APIKeys />
                  </PermissionRoute>
                }
              />
            </Route>
            
            {/* Redirecionamento padrão */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </PermissionsProvider>
    </AuthProvider>
  )
}

export default App
